import { Avatar as MuiAvatar, Box, BoxProps, Skeleton, useTheme } from '@mui/material';
import React from 'react';

import type { SxProps, Theme } from '../../themes/themes';
import { getStyles } from './AvatarCard.styles';

export interface AvatarCardProps extends BoxProps {
  imageComponent?: React.ReactElement;
  imageUrl?: string;
  imageAlt?: string;
  headline?: React.ReactElement | React.ReactElement[];
  text?: React.ReactNode;
  subline?: React.ReactNode;
  actions?: React.ReactNode;
  textBoxStyles?: SxProps;
  actionsStyles?: SxProps;
}

export const AvatarCardSkeleton = (): React.ReactElement => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);

  return (
    <Box sx={styles.wrapper}>
      <Skeleton variant="circular" sx={styles.avatar} />
      <Box sx={{ ...styles.textBox } as SxProps}>
        <Skeleton variant="text" sx={{ width: { xs: '100px', md: '350px' }, height: '24px' }} />
        <Skeleton variant="text" sx={{ width: { xs: '100px', md: '350px' }, height: '24px' }} />
        <Skeleton variant="text" sx={{ width: { xs: '100px', md: '350px' }, height: '24px' }} />
      </Box>
      <Skeleton variant="text" sx={{ width: { xs: '24px', lg: '350px' }, height: '24px' }} />
    </Box>
  );
};

export const AvatarCard: React.FunctionComponent<AvatarCardProps> = ({
  imageAlt,
  imageUrl,
  headline,
  text,
  subline,
  actions,
  textBoxStyles,
  actionsStyles,
  imageComponent,
  ...props
}: AvatarCardProps) => {
  const theme = useTheme<Theme>();
  const styles = getStyles(theme);
  const sx: BoxProps['sx'] = Object.assign({}, styles.wrapper, props.sx);

  return (
    <Box {...props} sx={sx}>
      {imageComponent ? (
        <MuiAvatar sx={styles.avatar}>{imageComponent}</MuiAvatar>
      ) : imageUrl ? (
        <MuiAvatar alt={imageAlt} src={imageUrl} sx={styles.avatar} />
      ) : null}
      <Box sx={{ ...styles.textBox, ...textBoxStyles } as SxProps}>
        {headline}
        {text}
        {subline}
      </Box>
      <Box sx={{ ...styles.actions, ...actionsStyles } as SxProps}>{actions}</Box>
    </Box>
  );
};
